import { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SafeImage from '@components/SafeImage';
import SafeVideo from '@components/SafeVideo';

const ARROW_BUTTON_STYLE = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
    '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
    zIndex: 1,
};

/**
 * Fullscreen-ish viewer for an array of media items.
 * Each item: { thumbnailUrl, videoUrl?, label? }. If videoUrl is set, the item is a video.
 *
 * Failure handling: when an item fails to load, its index is added to a `failed` set; arrow
 * navigation skips failed items, and if the *current* item fails the viewer auto-advances to
 * the next visible item (forward first, then back, then close if none remain). The `failed` set
 * is reset on every open so transient failures get another chance the next time the user opens.
 */
const MediaLightbox = ({ open, onClose, items, initialIndex = 0 }) => {
    const [index, setIndex] = useState(initialIndex);
    const [failed, setFailed] = useState(() => new Set());

    useEffect(() => {
        if (open) {
            setIndex(initialIndex);
            setFailed(new Set());
        }
    }, [open, initialIndex]);

    const total = items?.length ?? 0;

    const findVisible = useCallback(
        (from, direction) => {
            let i = from + direction;
            while (i >= 0 && i < total) {
                if (!failed.has(i)) return i;
                i += direction;
            }
            return null;
        },
        [total, failed],
    );

    const goPrev = useCallback(() => {
        const next = findVisible(index, -1);
        if (next !== null) setIndex(next);
    }, [findVisible, index]);

    const goNext = useCallback(() => {
        const next = findVisible(index, 1);
        if (next !== null) setIndex(next);
    }, [findVisible, index]);

    const markCurrentFailed = useCallback(() => {
        setFailed(prev => {
            const s = new Set(prev);
            s.add(index);
            return s;
        });
        const next = findVisible(index, 1) ?? findVisible(index, -1);
        if (next !== null) {
            setIndex(next);
        } else {
            onClose?.();
        }
    }, [findVisible, index, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const handler = e => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, goPrev, goNext, onClose]);

    if (!open || total === 0) return null;

    const current = items[index];
    const isVideo = !!current?.videoUrl;
    const mediaUrl = isVideo ? current.videoUrl : current?.thumbnailUrl;

    const hasPrev = findVisible(index, -1) !== null;
    const hasNext = findVisible(index, 1) !== null;

    const visibleCount = total - failed.size;
    const visiblePosition = items.slice(0, index + 1).filter((_, i) => !failed.has(i)).length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <Box
                sx={{
                    position: 'relative',
                    backgroundColor: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    maxHeight: '90vh',
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#fff',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
                        zIndex: 1,
                    }}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>

                {visibleCount > 1 && (
                    <>
                        <IconButton
                            onClick={goPrev}
                            disabled={!hasPrev}
                            sx={{ ...ARROW_BUTTON_STYLE, left: 8 }}
                            aria-label="previous"
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            onClick={goNext}
                            disabled={!hasNext}
                            sx={{ ...ARROW_BUTTON_STYLE, right: 8 }}
                            aria-label="next"
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}

                {isVideo ? (
                    <SafeVideo
                        key={mediaUrl}
                        src={mediaUrl}
                        controls
                        autoPlay
                        onError={markCurrentFailed}
                        style={{ maxWidth: '100%', maxHeight: '90vh', display: 'block' }}
                    />
                ) : (
                    <SafeImage
                        src={mediaUrl}
                        alt={current?.label || ''}
                        onError={markCurrentFailed}
                        style={{ maxWidth: '100%', maxHeight: '90vh', display: 'block', objectFit: 'contain' }}
                    />
                )}

                {visibleCount > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: '#fff',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                        }}
                    >
                        {visiblePosition} / {visibleCount}
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default MediaLightbox;
